import { DateTime } from 'luxon';
import { outcomeLabels, requestStatusLabels, utilitiesPresentLabels } from '~/common/dataRequestUtils';
import { type DataRequestV2, RequestStatus, RequestType } from '~/common/types';
import { compareAgencyRows, hasNoAssetsPresent, type UtilityAgency } from '../common';
import { buildAgencyRows, requestMatchesRow } from '../Table/grouping';

const formatDate = (date?: DateTime | Date) => {
  if (!date) return '';
  return DateTime.fromJSDate(new Date(date.toString())).toLocaleString(DateTime.DATE_SHORT);
};

interface Config {
  header: string;
  getValue: (a: UtilityAgency, r?: DataRequestV2) => string;
}

const isValidStatus = (r?: DataRequestV2) => {
  if (r && r.requestStatus !== RequestStatus.Dismissed) {
    return true;
  }

  return false;
};

const getAvailability = (a: UtilityAgency, key: keyof UtilityAgency) => {
  const noAssets = hasNoAssetsPresent(a);
  if (noAssets) return 'No Assets Present';
  return a[key] ? 'YES' : 'NO';
};

export const config: { [key: string]: Config } = {
  agencyName: {
    header: 'Agency Name',
    getValue: (a) => a.agencyName,
  },
  type: {
    header: 'Type',
    getValue: (a) => a.type,
  },
  availableDigitally: {
    header: 'Available Digitally on CivilGrid',
    getValue: (a) => getAvailability(a, 'availableDigitally'),
  },
  mapsAvailable: {
    header: 'Map Request on CivilGrid',
    getValue: (a) => getAvailability(a, 'dataRequestable'),
  },
  asbuiltsAvailable: {
    header: 'As-Built Request on CivilGrid',
    getValue: (a) => getAvailability(a, 'asbuiltsAvailable'),
  },
  lastRefresh: {
    header: 'Data Last\nRefresh Date',
    getValue: (a) => formatDate(a.lastRefresh),
  },
  sourceDate: {
    header: 'Data Source Date',
    getValue: (a) => formatDate(a.sourceDate),
  },
  mapsRequested: {
    header: 'Maps Requested',
    getValue: (a, r) => (r !== undefined ? 'X' : ''),
  },
  createdAt: {
    header: 'Map Request\nInitiated Date',
    getValue: (_, r) => formatDate(r?.createdAt),
  },
  requestStatus: {
    header: 'Map Request Status',
    getValue: (a, r) => (r?.requestStatus && isValidStatus(r) ? requestStatusLabels[r.requestStatus] : ''),
  },
  resolutionDate: {
    header: 'Map Request\nCompleted Date',
    getValue: (_, r) => formatDate(r?.resolutionDate),
  },
  utilityPresence: {
    header: 'Map Request\nUtility Presence',
    getValue: (a, r) => (r?.utilityPresence ? utilitiesPresentLabels[r.utilityPresence] : ''),
  },
  outcome: {
    header: 'Map Request\nOutcome',
    getValue: (_, r) => (r?.outcome ? outcomeLabels[r.outcome] : ''),
  },
  notes: {
    header: 'Map Request Notes',
    getValue: (a, r) => r?.notes || '',
  },
  firstContactedAt: {
    header: 'Map Request\n1st Contact Date',
    getValue: (_, r) => formatDate(r?.firstContactedAt),
  },
  secondContactedAt: {
    header: 'Map Request\n2nd Contact Date',
    getValue: (_, r) => formatDate(r?.secondContactedAt),
  },
  thirdContactedAt: {
    header: 'Map Request\n3rd Contact Date',
    getValue: (_, r) => formatDate(r?.thirdContactedAt),
  },
  fourthContactedAt: {
    header: 'Map Request\n4th Contact Date',
    getValue: (_, r) => formatDate(r?.fourthContactedAt),
  },
} as const;

export const asbuiltConfig: { [key: string]: Config } = {
  asbuiltsRequested: {
    header: 'As-Builts Requested',
    getValue: config.mapsRequested.getValue,
  },
  createdAt: {
    header: 'As-Builts Requested\nInitiated Date',
    getValue: config.createdAt.getValue,
  },
  requestStatus: {
    header: 'As-Builts\nRequest Status',
    getValue: config.requestStatus.getValue,
  },
  resolutionDate: {
    header: 'As-Builts Request\nCompleted Date',
    getValue: config.resolutionDate.getValue,
  },
  utilityPresence: {
    header: 'As-Builts Request\nUtility Presence',
    getValue: config.utilityPresence.getValue,
  },
  outcome: {
    header: 'As-Builts Request\nOutcome',
    getValue: config.outcome.getValue,
  },
  notes: {
    header: 'As-Builts Request\nNotes',
    getValue: config.notes.getValue,
  },
  firstContactedAt: {
    header: 'As-Builts Request\n1st Contact Date',
    getValue: config.firstContactedAt.getValue,
  },
  secondContactedAt: {
    header: 'As-Builts Request\n2nd Contact Date',
    getValue: config.secondContactedAt.getValue,
  },
  thirdContactedAt: {
    header: 'As-Builts Request\n3rd Contact Date',
    getValue: config.thirdContactedAt.getValue,
  },
  fourthContactedAt: {
    header: 'As-Builts Request\n4th Contact Date',
    getValue: config.fourthContactedAt.getValue,
  },
};

/**
 * Builds the Outreach Log rows the way the Utilities table lays them out: one row per agency, or
 * one per Region when its requests are split across sub-geometries. Each row pairs with the maps
 * and as-builts request in its own record, in the table's `compareAgencyRows` order.
 */
export function buildOutreachLogRows(agencies: UtilityAgency[], dataRequests: DataRequestV2[]) {
  // Keyed by `agencies.id`: `agencyId` is nullable and not unique.
  const requestsByAgency = new Map<string, DataRequestV2[]>();
  for (const request of dataRequests) {
    const list = requestsByAgency.get(request.agency.id) ?? [];
    list.push(request);
    requestsByAgency.set(request.agency.id, list);
  }

  return [...agencies].sort(compareAgencyRows).flatMap((agency) => {
    const requests = requestsByAgency.get(agency.id) ?? [];
    const [grouped] = buildAgencyRows([agency], requests);

    return (grouped.subRows ?? [grouped]).map((record) => {
      const row: { [key: string]: string } = {};
      const mapsReq = requests.find((r) => r.type === RequestType.MapsRequested && requestMatchesRow(r, record));
      const asbuiltsReq = requests.find(
        (r) => r.type === RequestType.AsbuiltsRequested && requestMatchesRow(r, record),
      );

      Object.keys(config).forEach((key) => {
        row[`maps-${key}`] = config[key].getValue(record, mapsReq);
      });

      Object.keys(asbuiltConfig).forEach((key) => {
        row[`asbuilts-${key}`] = asbuiltConfig[key].getValue(record, asbuiltsReq);
      });

      return row;
    });
  });
}

export const removeRegEx = (value: string) => {
  return value.replaceAll('\n', ' ').replaceAll('\r', ' ').replaceAll(',', ' ');
};

export function sanitizeFileName(originalName: string) {
  return originalName.replaceAll(/[^a-zA-Z0-9-_.]/g, '_').replaceAll('.', '_');
}

const headings = [
  ...Object.values(config).map((c) => removeRegEx(c.header)),
  ...Object.values(asbuiltConfig).map((c) => removeRegEx(c.header)),
];

export const generateCSV = (rows: { [key: string]: any }[]) => {
  let csvData = `${headings.join(', ')}\n`;

  rows.forEach((row) => {
    csvData += `${Object.values(row)
      .map((v) => removeRegEx(v))
      .join(', ')}\n`;
  });

  return csvData;
};

export const downloadCSV = (csv: string, name: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const fileName = `${sanitizeFileName(`Outreach Log - ${name} - ${new Date().toLocaleDateString()}`)}.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};
